import { toast } from "@/shared/hooks/useToast";
import { api } from "@/shared/libs/axiosClient";
import { useCallback, useMemo, useState } from "react";
import { buildVersioningScheduleSourceId } from "../libs/versioningSchedule";
import type {
    UpsertVersioningScheduleSourceDto,
    VersioningScheduleConfigs,
    VersioningScheduleModalMode,
    VersioningScheduleSourceRow,
} from "../types/mediaVersionUpdateAlert";

type SourcesResponse = {
    sources: Array<{ channel: string; url: string; selector: string }>;
};

type ConfigsResponse = {
    configs: VersioningScheduleConfigs;
};

export function useMediaVersionUpdateAlertSettings() {
    const [configs, setConfigs] = useState<VersioningScheduleConfigs>({ enable: true });

    const [rows, setRows] = useState<VersioningScheduleSourceRow[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<VersioningScheduleModalMode>("추가");
    const [editingId, setEditingId] = useState<string | null>(null);

    const [form, setForm] = useState<UpsertVersioningScheduleSourceDto>({
        channel: "",
        url: "",
        selector: "",
    });

    const resetForm = useCallback(() => {
        setForm({ channel: "", url: "", selector: "" });
    }, []);

    const load = useCallback(async () => {
        try {
            const [configsRes, sourcesRes] = await Promise.all([
                api.get<ConfigsResponse>("/versioning-schedule-check/configs"),
                api.get<SourcesResponse>("/versioning-schedule-check/sources"),
            ]);

            setConfigs(configsRes.configs);

            const nextRows: VersioningScheduleSourceRow[] = (sourcesRes.sources ?? [])
                .map((s) => ({
                    id: buildVersioningScheduleSourceId(s.channel),
                    channel: s.channel,
                    url: s.url,
                    selector: s.selector,
                }))
                .sort((a, b) => a.channel.localeCompare(b.channel));

            setRows(nextRows);
            setSelectedIds([]);
            resetForm();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "조회에 실패했습니다.";
            toast({ title: "조회 실패", description: msg, variant: "destructive" });
        }
    }, [resetForm]);

    const openAdd = useCallback(() => {
        setModalMode("추가");
        setEditingId(null);
        resetForm();
        setModalOpen(true);
    }, [resetForm]);

    const openEdit = useCallback((row: VersioningScheduleSourceRow) => {
        setModalMode("수정");
        setEditingId(row.id);
        setForm({ channel: row.channel, url: row.url, selector: row.selector });
        setModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        resetForm();
        setEditingId(null);
    }, [resetForm]);

    const submit = useCallback(async () => {
        const channel = form.channel.trim();
        const url = form.url.trim();
        const selector = form.selector.trim();

        if (!channel || !url || !selector) {
            toast({ title: "입력 오류", description: "매체명/URL/Selector는 필수입니다.", variant: "destructive" });
            return;
        }

        const originChannel =
            editingId ? rows.find((r) => r.id === editingId)?.channel ?? null : null;

        try {
            await api.post<void>("/versioning-schedule-check/sources", {
                channel,
                url,
                selector,
                origin_channel: originChannel, // 추가면 null, 수정이면 기존 채널명
            });

            toast({ title: "완료", description: `Versioning Schedule 확인 대상 ${modalMode} 완료` });
            closeModal();
            await load();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "저장에 실패했습니다.";
            toast({ title: "저장 실패", description: msg, variant: "destructive" });
        }
    }, [form, editingId, rows, modalMode, closeModal, load]);

    const deleteSelected = useCallback(async () => {
        if (selectedIds.length === 0) return;

        const channels = selectedIds
            .map((id) => rows.find((r) => r.id === id)?.channel)
            .filter((v): v is string => typeof v === "string" && v.trim() !== "");

        if (channels.length === 0) return;

        try {
            await api.delete<void>("/versioning-schedule-check/sources", {
                data: { channels },
            });

            toast({ title: "삭제 완료", description: "선택된 항목이 삭제되었습니다." });
            await load();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "삭제에 실패했습니다.";
            toast({ title: "삭제 실패", description: msg, variant: "destructive" });
        }
    }, [selectedIds, rows, load]);

    const toggleConfig = useCallback(async (key: keyof VersioningScheduleConfigs, checked: boolean) => {
        try {
            await api.patch<void>(`/versioning-schedule-check/configs/${key}`, { value: checked });
            setConfigs((prev) => ({ ...prev, [key]: checked }));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "설정 변경에 실패했습니다.";
            toast({ title: "설정 변경 실패", description: msg, variant: "destructive" });
        }
    }, []);

    const summary = useMemo(
        () => ({
            total: rows.length,
            selected: selectedIds.length,
        }),
        [rows.length, selectedIds.length]
    );

    return {
        configs,
        rows,
        selectedIds,
        setSelectedIds,

        modalOpen,
        modalMode,
        form,
        setForm,

        summary,

        load,
        openAdd,
        openEdit,
        closeModal,
        submit,
        deleteSelected,
        toggleConfig,
    };
}